import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges
} from '@angular/core';

/**
 * A user-moved position for a draggable element, in pixels, relative to the element's
 * offsetParent (its nearest positioned ancestor). `null` means "no move yet — use the
 * element's own CSS default (e.g. right/bottom)".
 */
export interface EtsDraggablePosition {
    left: number;
    top: number;
}

/**
 * EtsDraggable — makes any host element draggable with the pointer, clamped to its offsetParent.
 *
 * WHY this exists (CR-20260710-001 / A7): the CoPilot launcher buttons (FABs) on OPG / APG / the
 * Hedge Matrix are pinned to their grid's bottom-right and cannot be moved, so they cover the very
 * grid content the trader wants to read. This directive lets the user drag the FAB out of the way.
 *
 * Design points that matter for the FABs:
 *  - POINTER CAPTURE: on pointerdown we `setPointerCapture`, so every move/up/cancel for that pointer
 *    is delivered to the element even when the cursor leaves the element or the browser window. This
 *    is what stops a release-outside-the-window from stranding the drag. Listeners live on the element
 *    (not window) for the same reason.
 *  - CLICK vs DRAG: a press that moves less than the threshold stays a click (the FAB still opens the
 *    CoPilot); a real drag emits the new position AND swallows the click the browser fires next, so a
 *    drag can never open the popup.
 *  - SURVIVES *ngIf TEARDOWN: the FAB is destroyed when its popup opens and re-created on close, so the
 *    moved position must live on the HOST COMPONENT, not on this directive/DOM node. It is a two-way
 *    value: seed via [etsDraggablePosition], persist via (etsDraggablePositionChange). On re-mount the
 *    directive re-applies the persisted position.
 *  - CLAMPED to the offsetParent: for OPG/APG that parent is the whole panel; for the Hedge Matrix it
 *    is the matrix column (matching each FAB's default resting corner).
 *  - Session only: the host stores the value in a plain field, so a page reload resets to default.
 *
 * Generic, dependency-free (no @angular/cdk, which is not in this repo). Declared in its own tiny
 * module so all three panel modules can import it without any cross-folder feature coupling. Pair with
 * `touch-action: none` on the host element so touch/pen drags are not stolen by the browser as scrolls.
 */
@Directive({
    selector: '[etsDraggable]'
})
export class EtsDraggableDirective implements AfterViewInit, OnChanges, OnDestroy {

    constructor(
        private readonly _elementRef: ElementRef<HTMLElement>,
        private readonly _zone: NgZone
    ) {
    }

    /** Bare attribute (`etsDraggable`) or `true` enables dragging; `[etsDraggable]="false"` disables it. */
    @Input('etsDraggable') dragEnabled: boolean | string = true;

    /** Persisted position (px, relative to offsetParent). `null` => use the element's CSS default. */
    @Input() etsDraggablePosition: EtsDraggablePosition | null = null;

    /** Emitted once at the end of each real drag with the element's final offset. */
    @Output() etsDraggablePositionChange = new EventEmitter<EtsDraggablePosition>();

    /** Movement (px) below which a press is treated as a click, not a drag. */
    private static readonly DRAG_THRESHOLD_PX = 4;

    /** Keep the element at least this far inside its container so it can always be grabbed again. */
    private static readonly EDGE_MARGIN_PX = 4;

    private _pointerId: number | null = null;
    private _startPointerX = 0;
    private _startPointerY = 0;
    private _baseLeft = 0;
    private _baseTop = 0;
    private _moved = false;

    private readonly _onPointerDown = (ev: PointerEvent): void => this._handlePointerDown(ev);
    private readonly _onPointerMove = (ev: PointerEvent): void => this._handlePointerMove(ev);
    private readonly _onPointerUp = (ev: PointerEvent): void => this._handlePointerUp(ev);
    private readonly _onPointerCancel = (ev: PointerEvent): void => this._handlePointerCancel(ev);

    ngAfterViewInit(): void {
        // Register OUTSIDE Angular — a drag mutates inline styles directly and must not trigger change
        // detection on every pointermove. Listeners live on the ELEMENT; pointer capture (set on
        // pointerdown) routes all move/up/cancel for the active pointer here, even outside the window.
        this._zone.runOutsideAngular(() => {
            const el = this._elementRef.nativeElement;
            el.addEventListener('pointerdown', this._onPointerDown);
            el.addEventListener('pointermove', this._onPointerMove);
            el.addEventListener('pointerup', this._onPointerUp);
            el.addEventListener('pointercancel', this._onPointerCancel);
        });
        // Re-apply any persisted position on (re)mount — this is what makes the moved FAB survive its
        // own *ngIf teardown when the CoPilot popup opens and closes.
        this._applyPosition();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.etsDraggablePosition && !changes.etsDraggablePosition.firstChange) {
            this._applyPosition();
        }
    }

    ngOnDestroy(): void {
        const el = this._elementRef.nativeElement;
        el.removeEventListener('pointerdown', this._onPointerDown);
        el.removeEventListener('pointermove', this._onPointerMove);
        el.removeEventListener('pointerup', this._onPointerUp);
        el.removeEventListener('pointercancel', this._onPointerCancel);
    }

    private get _isEnabled(): boolean {
        // bare attribute arrives as '' — treat anything that is not an explicit false/'false' as enabled.
        return this.dragEnabled !== false && this.dragEnabled !== 'false';
    }

    private _applyPosition(): void {
        const pos = this.etsDraggablePosition;
        if (!pos) {
            return; // no user move — the element's CSS default (right/bottom) stands
        }
        const el = this._elementRef.nativeElement;
        const parent = (el.offsetParent as HTMLElement | null) || el.parentElement;
        if (!parent || parent.clientWidth === 0 || parent.clientHeight === 0) {
            // Not laid out yet (e.g. the panel is display:none). Do NOT write — clamping against a
            // zero-size box would jam the element into the corner. The CSS default holds until the
            // element is visible again and a later re-apply (or a fresh drag) runs.
            return;
        }
        this._writeOffset(this._clampToParent(pos.left, pos.top));
    }

    private _writeOffset(pos: EtsDraggablePosition): void {
        const el = this._elementRef.nativeElement;
        el.style.left = pos.left + 'px';
        el.style.top = pos.top + 'px';
        // Clear the default right/bottom anchor so left/top wins with no conflicting offsets.
        el.style.right = 'auto';
        el.style.bottom = 'auto';
    }

    private _clampToParent(left: number, top: number): EtsDraggablePosition {
        const el = this._elementRef.nativeElement;
        const parent = (el.offsetParent as HTMLElement | null) || el.parentElement;
        const m = EtsDraggableDirective.EDGE_MARGIN_PX;
        if (!parent) {
            return {left, top};
        }
        const maxLeft = Math.max(m, parent.clientWidth - el.offsetWidth - m);
        const maxTop = Math.max(m, parent.clientHeight - el.offsetHeight - m);
        return {
            left: Math.min(Math.max(left, m), maxLeft),
            top: Math.min(Math.max(top, m), maxTop)
        };
    }

    private _handlePointerDown(ev: PointerEvent): void {
        if (!this._isEnabled || ev.button !== 0) {
            return; // only the primary button starts a drag
        }
        if (this._pointerId !== null) {
            return; // a drag is already in progress — a second pointer must not hijack it
        }
        const el = this._elementRef.nativeElement;
        this._pointerId = ev.pointerId;
        this._startPointerX = ev.clientX;
        this._startPointerY = ev.clientY;
        // Base offset = where the element currently sits within its offsetParent. offsetLeft/offsetTop
        // report this whether the element is positioned via right/bottom (default) or a prior left/top.
        this._baseLeft = el.offsetLeft;
        this._baseTop = el.offsetTop;
        this._moved = false;
        // Capture the pointer so move/up/cancel are delivered here even if it leaves the element or the
        // window. Best-effort — a browser may refuse (e.g. detached node); the drag still works locally.
        try {
            el.setPointerCapture(ev.pointerId);
        } catch {
            /* capture is best-effort */
        }
    }

    private _handlePointerMove(ev: PointerEvent): void {
        if (this._pointerId === null || ev.pointerId !== this._pointerId) {
            return;
        }
        const dx = ev.clientX - this._startPointerX;
        const dy = ev.clientY - this._startPointerY;

        if (!this._moved
            && Math.abs(dx) < EtsDraggableDirective.DRAG_THRESHOLD_PX
            && Math.abs(dy) < EtsDraggableDirective.DRAG_THRESHOLD_PX) {
            return; // still within the click threshold
        }
        this._moved = true;
        ev.preventDefault(); // suppress text selection while dragging

        this._writeOffset(this._clampToParent(this._baseLeft + dx, this._baseTop + dy));
    }

    private _handlePointerUp(ev: PointerEvent): void {
        if (this._pointerId === null || ev.pointerId !== this._pointerId) {
            return;
        }
        const moved = this._moved;
        this._endGesture(ev.pointerId);
        if (moved) {
            // A real drag ended. Swallow the click the browser fires next (so it can't open the popup)
            // and persist the final offset — the host stores it and it survives the FAB's *ngIf teardown.
            this._suppressNextClick();
            this._emitCurrentOffset();
        }
    }

    private _handlePointerCancel(ev: PointerEvent): void {
        if (this._pointerId === null || ev.pointerId !== this._pointerId) {
            return;
        }
        const moved = this._moved;
        this._endGesture(ev.pointerId);
        // A cancelled pointer produces NO synthetic click, so no click to suppress; but the element did
        // move, so persist what is visible to keep the stored value consistent with the screen.
        if (moved) {
            this._emitCurrentOffset();
        }
    }

    private _endGesture(pointerId: number): void {
        const el = this._elementRef.nativeElement;
        try {
            if (el.hasPointerCapture(pointerId)) {
                el.releasePointerCapture(pointerId);
            }
        } catch {
            /* release is best-effort */
        }
        this._pointerId = null;
        this._moved = false;
    }

    private _emitCurrentOffset(): void {
        const el = this._elementRef.nativeElement;
        const finalPos: EtsDraggablePosition = {left: el.offsetLeft, top: el.offsetTop};
        this._zone.run(() => this.etsDraggablePositionChange.emit(finalPos));
    }

    /**
     * A real drag is followed by a synthetic click on the element. Swallow exactly that click on the
     * capture phase (which runs before the element's own bubble-phase (click) handler) so a drag can
     * never fire openCopilot(). Scoped to this element and self-removing; the timeout is a fallback for
     * "drag ended but no click was produced".
     */
    private _suppressNextClick(): void {
        const el = this._elementRef.nativeElement;
        const suppressor = (ev: Event): void => {
            if (ev.target === el || el.contains(ev.target as Node)) {
                ev.stopPropagation();
                ev.preventDefault();
            }
            document.removeEventListener('click', suppressor, true);
        };
        document.addEventListener('click', suppressor, true);
        window.setTimeout(() => document.removeEventListener('click', suppressor, true), 350);
    }
}
